import React, { useState, useRef, useEffect } from 'react';
import ReusableTable from '../dashboard/ReusableTable';
import TaskCreationForm from './TaskCreationForm';
import { Dialog } from 'primereact/dialog';
import { getRequest, postRequest, putRequest, deleteRequest } from '../services/apiService';
import UploadTaskForm from './UploadTaskForm';
import ReusableForm from '../common/ReusableForm';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';

const MileStones = () => {
  // Initialize all required state variables
  const [tasks, setTasks] = useState([
    {
      id: 1,
      name: "Hull Integrity Testing",
      description: "Perform structural tests on the hull sections of...",
      project: "Project Vikrant",
      assignee: "Lt. Kumar",
      status: "In Progress",
      priority: "High",
      startDate: "2025-03-02",
      endDate: "2025-03-30",
      delay: 0,
      type: "Start to Start",
      precedingActivity: null,
      succeedingActivity: null,
      succeedingDelay: 0,
      succeedingStartDate: "",
      succeedingEndDate: "",
      level: 0, // Top level task
      parentId: null, // No parent
      children: [2, 3], // Child task IDs
      isExpanded: true
    },
    {
      id: 2,
      name: "Structural Analysis",
      description: "Analyze structural integrity of hull sections",
      project: "Project Vikrant",
      assignee: "Lt. Kumar",
      status: "In Progress",
      priority: "High",
      startDate: "2025-03-02",
      endDate: "2025-03-15",
      delay: 0,
      type: "Start to Start",
      precedingActivity: null,
      succeedingActivity: null,
      succeedingDelay: 0,
      succeedingStartDate: "",
      succeedingEndDate: "",
      level: 1, // First level indentation
      parentId: 1, // Parent task ID
      children: [4, 5], // Child task IDs
      isExpanded: true
    },
    {
      id: 3,
      name: "Material Testing",
      description: "Test materials used in hull construction",
      project: "Project Vikrant",
      assignee: "Lt. Kumar",
      status: "Pending",
      priority: "High",
      startDate: "2025-03-16",
      endDate: "2025-03-30",
      delay: 0,
      type: "Start to Start",
      precedingActivity: 2,
      succeedingActivity: null,
      succeedingDelay: 0,
      succeedingStartDate: "",
      succeedingEndDate: "",
      level: 1, // First level indentation
      parentId: 1, // Parent task ID
      children: [], // No children
      isExpanded: true
    },
    {
      id: 4,
      name: "Load Testing",
      description: "Perform load tests on structural components",
      project: "Project Vikrant",
      assignee: "Lt. Kumar",
      status: "In Progress",
      priority: "High",
      startDate: "2025-03-02",
      endDate: "2025-03-10",
      delay: 0,
      type: "Start to Start",
      precedingActivity: null,
      succeedingActivity: null,
      succeedingDelay: 0,
      succeedingStartDate: "",
      succeedingEndDate: "",
      level: 2, // Second level indentation
      parentId: 2, // Parent task ID
      children: [], // No children
      isExpanded: true
    },
    {
      id: 5,
      name: "Stress Analysis",
      description: "Analyze stress points in the structure",
      project: "Project Vikrant",
      assignee: "Lt. Kumar",
      status: "Pending",
      priority: "High",
      startDate: "2025-03-11",
      endDate: "2025-03-15",
      delay: 0,
      type: "Start to Start",
      precedingActivity: 4,
      succeedingActivity: null,
      succeedingDelay: 0,
      succeedingStartDate: "",
      succeedingEndDate: "",
      level: 2, // Second level indentation
      parentId: 2, // Parent task ID
      children: [], // No children
      isExpanded: true
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFullTable, setShowFullTable] = useState(false);
  const [showFullGantt, setShowFullGantt] = useState(false);
  const [activeTab, setActiveTab] = useState('Tasks');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState(0.5);
  const [dateRange, setDateRange] = useState({
    start: new Date('2025-03-02'),
    end: new Date('2025-03-30'),
    original: {
      start: new Date('2025-03-02'),
      end: new Date('2025-03-30')
    }
  });

  const [selectedDateOption, setSelectedDateOption] = useState('delay'); // 'delay' or 'manual'
  const [succeedingDateOption, setSucceedingDateOption] = useState('delay'); // 'delay' or 'manual'

  const ganttRef = useRef(null);

  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    project: '',
    assignee: '',
    status: 'Pending',
    priority: 'Medium',
    startDate: '',
    endDate: '',
    delay: 0,
    type: 'Start to Start',
    precedingActivity: null,
    succeedingActivity: null,
    succeedingDelay: 0,
    succeedingStartDate: '',
    succeedingEndDate: '',
  });

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });

  // Delete confirmation form fields
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Completed':
        return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Completed</span>;
      case 'In Progress':
        return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">In Progress</span>;
      case 'Delayed':
        return <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">Delayed</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">Pending</span>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'High':
        return <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">High</span>;
      case 'Medium':
        return <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">Medium</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Low</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;
  };

  const handleToggleTask = (taskId) => {
    setTasks(prevTasks => {
      return prevTasks.map(task => {
        if (task.id === taskId) {
          return { ...task, isExpanded: !task.isExpanded };
        }
        return task;
      });
    });
  };

  const getFilteredTasksWithHierarchy = () => {
    const filtered = tasks.filter(task => {
      const matchesSearch = task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.assignee.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.project.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
      const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;

      // Check if parent is expanded
      if (task.parentId) {
        const parent = tasks.find(t => t.id === task.parentId);
        if (parent && !parent.isExpanded) {
          return false;
        }
      }

      return matchesSearch && matchesStatus && matchesPriority;
    });

    return filtered;
  };
  
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const response = await getRequest('/master/milestones/');
        if (!response.ok) {
          throw new Error('Failed to fetch tasks');
        }
        const data = await response.json();
        setTasks(data.map(task => ({
          ...task,
          isExpanded: true,
          children: task.children || [],
          level: task.level || 0
        })));
        
        // Set initial date range based on tasks
        if (data.length > 0) {
          const startDates = data.map(t => new Date(t.startDate));
          const endDates = data.map(t => new Date(t.endDate));
          const minStart = new Date(Math.min(...startDates));
          const maxEnd = new Date(Math.max(...endDates));
          
          setDateRange({
            start: minStart,
            end: maxEnd,
            original: {
              start: minStart,
              end: maxEnd
            }
          });
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  const handleNewTaskChange = (e) => {
    const { name, value } = e.target;
    setCurrentTask(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculateStartDate = (delay) => {
    const projectStartDate = new Date('2025-03-02');
    projectStartDate.setDate(projectStartDate.getDate() + parseInt(delay));
    return projectStartDate.toISOString().split('T')[0];
  };

  const calculateEndDate = (delay) => {
    const projectStartDate = new Date('2025-03-02');
    projectStartDate.setDate(projectStartDate.getDate() + parseInt(delay) + 28);
    return projectStartDate.toISOString().split('T')[0];
  };

  const calculateSucceedingDates = (delay) => {
    const precedingTask = tasks.find((task) => task.id === currentTask?.precedingActivity);
    if (!precedingTask) return { startDate: '', endDate: '' };

    const precedingEndDate = new Date(precedingTask.endDate);
    const startDate = new Date(precedingEndDate);
    startDate.setDate(startDate.getDate() + parseInt(delay));
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 28);

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  };

  const handleAddTask = () => {
    setCurrentTask({
      name: '',
      description: '',
      project: 'Project Vikrant',
      assignee: '',
      status: 'Pending',
      priority: 'Medium',
      startDate: '',
      endDate: '',
      delay: 0,
      type: 'Start to Start',
      precedingActivity: null,
      succeedingActivity: null,
      succeedingDelay: 0,
      succeedingStartDate: '',
      succeedingEndDate: '',
      level: 0,
      parentId: null,
      children: [],
      isExpanded: true
    });
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setCurrentTask(null);
  };

  const handleSubmitTask = async (taskData) => {
    try {
      const response = await postRequest('/master/milestones/', taskData);
      if (!response.ok) {
        throw new Error('Failed to create task');
      }
      const newTask = await response.json();
      
      setTasks(prevTasks => [...prevTasks, {
        ...newTask,
        isExpanded: true,
        children: newTask.children || [],
        level: newTask.level || 0
      }]);
      
      handleCloseAddModal();
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err.message);
    }
  };

  const handleEdit = (task) => {
    setCurrentTask(task);
    setShowEditModal(true);
  };

  const handleUpdateTask = async () => {
    try {
      const updatedTask = {
        ...currentTask,
        startDate: selectedDateOption === 'delay' ? calculateStartDate(currentTask.delay) : currentTask.startDate,
        endDate: selectedDateOption === 'delay' ? calculateEndDate(currentTask.delay) : currentTask.endDate,
        succeedingStartDate: succeedingDateOption === 'delay' ? calculateSucceedingDates(currentTask.succeedingDelay).startDate : currentTask.succeedingStartDate,
        succeedingEndDate: succeedingDateOption === 'delay' ? calculateSucceedingDates(currentTask.succeedingDelay).endDate : currentTask.succeedingEndDate,
      };

      const response = await putRequest(`/master/milestones/${currentTask.id}`, updatedTask);
      if (!response.ok) {
        throw new Error('Failed to update task');
      }
      const data = await response.json();

      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === currentTask.id ? {
            ...data,
            isExpanded: task.isExpanded,
            children: task.children,
            level: task.level
          } : task
        )
      );
      
      setShowEditModal(false);
      setCurrentTask(null);
    } catch (err) {
      console.error('Error updating task:', err);
      setError(err.message);
    }
  };

  const openDeleteModal = (task) => {
    setTaskToDelete(task);
    setDeleteFormData({ confirmation: '' });
    setDeleteDialogVisible(true);
  };

  const confirmDelete = async () => {
    if (deleteFormData.confirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!taskToDelete) return;

    try {
      setLoading(true);
      const response = await deleteRequest(`/master/milestones/${taskToDelete.id}`);
      if (!response.ok) {
        throw new Error('Failed to delete task');
      }
      
      setTasks(tasks.filter((task) => task.id !== taskToDelete.id));
      toast.success('Task deleted successfully.');
    } catch (err) {
      console.error('Error deleting task:', err);
      toast.error('Failed to delete task: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setDeleteDialogVisible(false);
      setTaskToDelete(null);
      setDeleteFormData({ confirmation: '' });
    }
  };

  const handleDelete = (task) => {
    openDeleteModal(task);
  };

  const columnFields = [
    {
      field: 'name',
      header: 'Task Name',
      body: (rowData) => (
        <div className="flex items-center">
          <div style={{ marginLeft: `${rowData.level * 20}px` }} className="flex items-center">
            {rowData.children && rowData.children.length > 0 && (
              <button
                onClick={() => handleToggleTask(rowData.id)}
                className="mr-2 text-gray-500 hover:text-gray-700"
              >
                {rowData.isExpanded ? '▼' : '▶'}
              </button>
            )}
            <span className="text-sm">{rowData.name}</span>
          </div>
        </div>
      )
    },
    { field: 'project', header: 'Project' },
    { field: 'assignee', header: 'Assignee' },
    {
      field: 'status',
      header: 'Status',
      body: (rowData) => getStatusBadge(rowData.status)
    },
    {
      field: 'priority',
      header: 'Priority',
      body: (rowData) => getPriorityBadge(rowData.priority)
    },
    {
      field: 'startDate',
      header: 'Start Date',
      body: (rowData) => formatDate(rowData.startDate)
    },
    {
      field: 'endDate',
      header: 'End Date',
      body: (rowData) => formatDate(rowData.endDate)
    },
    { field: 'type', header: 'Type' },
    
  ];
  const handleZoom = (type, e) => {
    // Calculate new zoom level
    const newZoomLevel = type === 'in'
      ? Math.min(zoomLevel + 0.25, 2)
      : Math.max(zoomLevel - 0.25, 0.5);

    // Set zoom origin based on mouse position or default to center
    let zoomOriginPoint = 0.5;
    if (e && e.nativeEvent) {
      const container = ganttRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const x = (e.nativeEvent.clientX - rect.left) / rect.width;
        zoomOriginPoint = Math.max(0, Math.min(1, x)); // Ensure between 0 and 1
      }
    }
    setZoomOrigin(zoomOriginPoint);

    // Calculate new date range based on zoom
    const currentRange = dateRange;
    const originalRangeDuration = (currentRange.original.end - currentRange.original.start) / (1000 * 60 * 60 * 24);
    const newRangeDuration = originalRangeDuration / newZoomLevel;

    // Calculate the focus point in time based on the zoom origin
    const currentRangeDuration = (currentRange.end - currentRange.start) / (1000 * 60 * 60 * 24);
    const focusPointDate = new Date(currentRange.start.getTime() + (currentRangeDuration * zoomOriginPoint * 24 * 60 * 60 * 1000));

    // Calculate new start and end dates while keeping the focus point at the same relative position
    const newStartDays = newRangeDuration * zoomOriginPoint;
    const newStart = new Date(focusPointDate.getTime() - (newStartDays * 24 * 60 * 60 * 1000));
    const newEnd = new Date(newStart.getTime() + (newRangeDuration * 24 * 60 * 60 * 1000));

    // Update date range
    setDateRange({
      start: newStart,
      end: newEnd,
      original: currentRange.original
    });

    // Start animation
    setIsZooming(true);
    setZoomLevel(newZoomLevel);

    // End animation after transition completes
    setTimeout(() => {
      setIsZooming(false);
    }, 300);
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setDateRange({
      start: new Date(dateRange.original.start),
      end: new Date(dateRange.original.end),
      original: dateRange.original
    });
    setIsZooming(true);
    setTimeout(() => {
      setIsZooming(false);
    }, 300);
  };

  useEffect(() => {
    // Add keyboard shortcuts for zooming (Ctrl/Cmd + plus/minus)
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '+') {
        e.preventDefault();
        handleZoom('in');
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        handleZoom('out');
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        resetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomLevel]);

  useEffect(() => {
    // Add custom CSS styles for zoom animations
    const style = document.createElement('style');
    style.textContent = `
      .gantt-container {
        position: relative;
      }
      .gantt-timeline {
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .gantt-timeline.zooming {
        transition: transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
      }
      .gantt-bar {
        transition: all 0.3s ease;
      }
      .gantt-zoom-controls {
        position: absolute;
        right: 16px;
        bottom: 16px;
        display: flex;
        align-items: center;
        background: white;
        border-radius: 20px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10;
        overflow: hidden;
        padding: 4px;
      }
      .gantt-zoom-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background: white;
        border: none;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.2s;
        color: #3730a3;
      }
      .gantt-zoom-btn:hover {
        background: #f3f4f6;
      }
      .gantt-zoom-btn:active {
        transform: scale(0.95);
      }
      .gantt-zoom-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .gantt-zoom-text {
        padding: 0 8px;
        font-size: 13px;
        font-weight: 500;
        color: #3730a3;
      }
      @media (max-width: 640px) {
        .gantt-zoom-controls {
          right: 8px;
          bottom: 8px;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const ganttContainer = ganttRef.current;

    const handleDoubleClick = () => {
      resetZoom();
    };

    if (ganttContainer) {
      ganttContainer.addEventListener('dblclick', handleDoubleClick);
    }

    return () => {
      if (ganttContainer) {
        ganttContainer.removeEventListener('dblclick', handleDoubleClick);
      }
    };
  }, [ganttRef.current]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-500';
      case 'In Progress':
        return 'bg-blue-500';
      case 'Delayed':
        return 'bg-red-500';
      default:
        return 'bg-yellow-500';
    }
  };

  const getChartConfig = () => {
    const sortedTasks = [...tasks].sort((a, b) => a.level - b.level);

    return {
      series: [{
        data: sortedTasks.map(task => ({
          x: task.name,
          y: [
            new Date(task.startDate).getTime(),
            new Date(task.endDate).getTime()
          ],
          fillColor: getStatusColor(task.status),
          strokeColor: '#000',
          meta: {
            status: task.status,
            assignee: task.assignee,
            description: task.description,
            level: task.level,
            parentId: task.parentId,
            isExpanded: task.isExpanded,
            hasChildren: task.children && task.children.length > 0
          }
        }))
      }],
      chart: {
        height: 350,
        type: 'rangeBar',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true
        },
        events: {
          click: function (event, chartContext, config) {
            if (config.dataPointIndex !== undefined) {
              const task = sortedTasks[config.dataPointIndex];
              if (task.children && task.children.length > 0) {
                handleToggleTask(task.id);
              }
            }
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '15%',
          borderRadius: 4,
          dataLabels: {
            hideOverflowingLabels: true,
            position: 'center'
          }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val, opts) {
          const task = sortedTasks[opts.dataPointIndex];
          if (!task) return '';
          const indent = ' '.repeat(task.level * 4);
          const prefix = task.children && task.children.length > 0
            ? (task.isExpanded ? '▼ ' : '▶ ')
            : '  ';
          return indent + prefix + task.name;
        },
        style: {
          colors: ['#fff'],
          fontSize: '10px'
        }
      },
      xaxis: {
        type: 'datetime',
        labels: {
          formatter: function (val) {
            return new Date(val).toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
          },
          style: {
            fontSize: '10px'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '10px'
          },
          formatter: function (val) {
            const task = sortedTasks.find(t => t.name === val);
            if (!task) return val;
            const indent = ' '.repeat(task.level * 4);
            return indent + val;
          }
        }
      },
      grid: {
        row: {
          colors: ['#f3f4f6', '#fff'],
          opacity: 0.5
        }
      },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const task = sortedTasks[dataPointIndex];
          if (!task) return '';

          return `
            <div class="p-2 bg-white border rounded shadow">
              <div class="font-semibold">${task.name}</div>
              <div class="text-sm text-gray-600">${task.description}</div>
              <div class="flex items-center mt-2">
                <div class="w-2 h-2 rounded-full ${getStatusColor(task.status)} mr-2"></div>
                <span class="text-sm">${task.status}</span>
              </div>
              <div class="text-sm mt-1">Assignee: ${task.assignee}</div>
              <div class="text-sm">${formatDate(task.startDate)} - ${formatDate(task.endDate)}</div>
              ${task.children && task.children.length > 0 ? `
                <div class="text-xs mt-2 text-gray-500">
                  Click to ${task.isExpanded ? 'collapse' : 'expand'} subtasks
                </div>
              ` : ''}
            </div>
          `;
        }
      }
    };
  };

  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
  };

  return (
    <div className="bg-gray-50 min-h-screen p-4">
      <div className="max-w-full mx-auto space-y-4">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-800">Tasks & Milestones</h1>
              <p className="text-sm text-gray-500">Manage project tasks and track milestones</p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleUploadClick}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md inline-flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                </svg>
                Upload
              </button>
              <button
                onClick={handleAddTask}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md inline-flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add New
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[240px]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search tasks, assignees, or projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex gap-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="Completed">Completed</option>
                <option value="In Progress">In Progress</option>
                <option value="Pending">Pending</option>
                <option value="Delayed">Delayed</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="border border-gray-300 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Priority</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabs and Content */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200">
            <div className="px-4 flex justify-between items-center">
              <nav className="-mb-px flex space-x-8">
                {['Tasks', 'Milestones'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`
                      whitespace-nowrap py-4 border-b-2 font-medium text-sm
                      ${activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {activeTab === 'Tasks' ? (
            <div className="flex gap-4 p-4">
              {/* Left side - Table */}
              <div className="w-1/2">
                <div className="relative bg-white rounded-lg shadow">
                  <div className="flex justify-between items-center p-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                    <h2 className="text-base font-semibold text-gray-800">Task List</h2>
                    <button
                      onClick={() => setShowFullTable(true)}
                      className="text-gray-500 hover:text-gray-700"
                      title="Expand Table"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5h-4m4 0v-4m0 4l-5-5" />
                      </svg>
                    </button>
                  </div>
                  <div className="h-[calc(90vh-120px)] overflow-auto">
                    <ReusableTable
                      title=""
                      data={getFilteredTasksWithHierarchy()}
                      setData={setTasks}
                      columnFields={columnFields}
                      rows={8}
                      colorScheme={{
                        headerBgColor: '#1e40af',
                        headerTextColor: '#ffffff',
                        tableBgColor: '#f3f4f6',
                        tableTextColor: '#1f2937',
                      }}
                      hideHeader={true}
                      actionMenu={true}
                      // actionItems={[
                      //   {
                      //     label: 'Edit',
                      //     icon: 'pi pi-pencil',
                      //     command: (rowData) => handleEdit(rowData)
                      //   },
                      //   {
                      //     label: 'Delete',
                      //     icon: 'pi pi-trash',
                      //     command: (rowData) => handleDelete(rowData)
                      //   }
                      // ]}
                    />
                  </div>
                </div>
              </div>

              {/* Right side - Gantt Chart */}
              <div className="w-1/2">
                <div className="relative bg-white rounded-lg shadow">
                  <div className="flex justify-between items-center p-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                    <h2 className="text-base font-semibold text-gray-800">Project Timeline</h2>
                    <button
                      onClick={() => setShowFullGantt(true)}
                      className="text-gray-500 hover:text-gray-700"
                      title="Expand Gantt Chart"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5h-4m4 0v-4m0 4l-5-5" />
                      </svg>
                    </button>
                  </div>
                  <div className="h-[calc(90vh-120px)] overflow-auto gantt-container" ref={ganttRef}>
                    <div className="sticky top-0 z-10 bg-white border-b">
                      <div className="flex">
                        <div className="w-44 py-2 px-3 border-r bg-gray-50 flex items-center">
                          <span className="text-xs font-medium text-gray-600">Task Details</span>
                        </div>
                        <div className="flex-1 overflow-x-auto">
                          <div
                            className={`flex px-3 py-2 bg-gray-50 gantt-timeline ${isZooming ? 'zooming' : ''}`}
                            style={{
                              minWidth: `${300 * zoomLevel}px`,
                              transformOrigin: `${zoomOrigin * 100}% center`
                            }}
                          >
                            {(() => {
                              const rangeDays = (dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24);
                              const divisions = Math.min(Math.floor(rangeDays), 6);
                              const dayStep = rangeDays / divisions;

                              return Array.from({ length: divisions }).map((_, index) => {
                                const date = new Date(dateRange.start);
                                date.setDate(date.getDate() + (index * dayStep));
                                return (
                                  <div
                                    key={index}
                                    className="flex-1 text-center relative group"
                                    style={{ width: `${100 * zoomLevel}px` }}
                                  >
                                    <div className="text-[10px] font-medium text-gray-500">
                                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-gray-200 cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      {tasks.map((task, taskIndex) => {
                        const startDate = new Date(task.startDate);
                        const endDate = new Date(task.endDate);
                        const currentRangeStart = dateRange.start;
                        const currentRangeEnd = dateRange.end;
                        const totalDays = (currentRangeEnd - currentRangeStart) / (1000 * 60 * 60 * 24);
                        const taskStart = (startDate - currentRangeStart) / (1000 * 60 * 60 * 24);
                        const taskDuration = (endDate - startDate) / (1000 * 60 * 60 * 24);
                        const startPercentage = Math.max(0, (taskStart / totalDays) * 100);
                        const widthPercentage = Math.min(100 - startPercentage, (taskDuration / totalDays) * 100);

                        const getStatusStyles = (status) => {
                          switch (status) {
                            case 'Completed':
                              return 'bg-gradient-to-r from-green-500 to-green-400 border-green-600';
                            case 'In Progress':
                              return 'bg-gradient-to-r from-blue-500 to-blue-400 border-blue-600';
                            case 'Delayed':
                              return 'bg-gradient-to-r from-red-500 to-red-400 border-red-600';
                            default:
                              return 'bg-gradient-to-r from-yellow-500 to-yellow-400 border-yellow-600';
                          }
                        };

                        return (
                          <div key={task.id}
                            className={`flex hover:bg-gray-50 group border-b ${taskIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                            <div className="w-44 py-2.5 px-3 border-r flex items-center">
                              <div className="min-w-0">
                                <div className="flex items-center space-x-2">
                                  <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                  <div className="text-xs font-medium text-gray-900 truncate">
                                    {task.name}
                                  </div>
                                </div>
                                <div className="mt-1 flex items-center space-x-2">
                                  <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-[9px] text-gray-600 font-medium">
                                      {task.assignee.split(' ')[1][0]}
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-gray-500 truncate">
                                    {formatDate(task.startDate)}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex-1 p-2 relative">
                              <div className="absolute inset-0 flex items-center px-2">
                                {task.precedingActivity && (
                                  <div
                                    className="absolute h-0.5 bg-gray-300"
                                    style={{
                                      left: `${startPercentage - 2}%`,
                                      width: '2%',
                                      top: '50%',
                                      transform: 'translateY(-50%)'
                                    }}
                                  ></div>
                                )}
                                <div
                                  className={`gantt-bar h-5 rounded-full shadow-sm border 
                                    ${getStatusStyles(task.status)}
                                    group-hover:shadow-md relative overflow-hidden`}
                                  style={{
                                    left: `${startPercentage}%`,
                                    width: `${widthPercentage}%`,
                                    transform: `scaleX(${zoomLevel})`,
                                    transformOrigin: 'left'
                                  }}
                                >
                                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-5 transition-opacity"></div>
                                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="text-[10px] text-white font-medium px-1.5 truncate shadow-sm">
                                      {`${formatDate(task.startDate)} - ${formatDate(task.endDate)}`}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="sticky bottom-0 border-t bg-gradient-to-r from-gray-50 to-white p-2 flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span className="text-[10px] font-medium text-gray-600">
                          {tasks.length} Tasks
                        </span>
                      </div>
                      <div className="flex space-x-4 text-[10px] text-gray-500">
                        <span className="font-medium">Start: {dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="font-medium">End: {dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    </div>

                    {/* Modern Zoom Controls for Regular View */}
                    <div className="gantt-zoom-controls">
                      <button
                        className="gantt-zoom-btn"
                        onClick={(e) => handleZoom('out', e)}
                        disabled={zoomLevel <= 0.5}
                        title="Zoom Out (Ctrl -)"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12H19" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <div className="gantt-zoom-text">
                        {Math.round(zoomLevel * 100)}%
                      </div>
                      <button
                        className="gantt-zoom-btn"
                        onClick={(e) => handleZoom('in', e)}
                        disabled={zoomLevel >= 2}
                        title="Zoom In (Ctrl +)"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5L12 19" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M5 12L19 12" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <button
                        className="gantt-zoom-btn ml-1"
                        onClick={resetZoom}
                        title="Reset Zoom (Ctrl 0)"
                        disabled={zoomLevel === 1 &&
                          dateRange.start.getTime() === dateRange.original.start.getTime() &&
                          dateRange.end.getTime() === dateRange.original.end.getTime()}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasks.map((task) => (
                <div key={task.id} className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col space-y-2">
                    <h3 className="font-medium text-gray-900">{task.name}</h3>
                    <p className="text-xs text-gray-500">{task.description}</p>
                    <div className="text-sm text-gray-900">{task.project}</div>
                    <div className="text-sm text-gray-900">{task.assignee}</div>
                    <div className="text-sm text-gray-500">{getStatusBadge(task.status)}</div>
                    <div className="text-sm text-gray-500">{getPriorityBadge(task.priority)}</div>
                    <div className="text-sm text-gray-900"><strong>Start Date:</strong> {formatDate(task.startDate)}</div>
                    <div className="text-sm text-gray-900"><strong>End Date:</strong> {formatDate(task.endDate)}</div>
                    <div className="text-sm text-gray-900"><strong>Type:</strong> {task.type}</div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(task)}
                        className="text-blue-600 hover:text-blue-900 text-xs font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(task)}
                        className="text-red-600 hover:text-red-900 text-xs font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full-screen Table Dialog */}
      <Dialog
        visible={showFullTable}
        onHide={() => setShowFullTable(false)}
        style={{ width: '95vw', height: '90vh' }}
        maximizable
        modal
        className="p-0"
        header="Task List"
        contentClassName="h-full"
      >
        <div className="h-full bg-white rounded-lg">
          <div className="p-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <h2 className="text-base font-semibold text-gray-800">Task List</h2>
          </div>
          <div className="h-[calc(90vh-120px)] overflow-auto">
            <ReusableTable
              title=""
              data={getFilteredTasksWithHierarchy()}
              setData={setTasks}
              columnFields={columnFields}
              rows={8}
              colorScheme={{
                headerBgColor: '#1e40af',
                headerTextColor: '#ffffff',
                tableBgColor: '#f3f4f6',
                tableTextColor: '#1f2937',
              }}
              hideHeader={true}
              actionMenu={true}
              actionItems={[
                {
                  label: 'Edit',
                  icon: 'pi pi-pencil',
                  command: (rowData) => handleEdit(rowData)
                },
                {
                  label: 'Delete',
                  icon: 'pi pi-trash',
                  command: (rowData) => handleDelete(rowData)
                }
              ]}
            />
          </div>
        </div>
      </Dialog>

      {/* Full-screen Gantt Dialog */}
      <Dialog
        visible={showFullGantt}
        onHide={() => setShowFullGantt(false)}
        style={{ width: '95vw', height: '90vh' }}
        maximizable
        modal
        className="p-0"
        header="Project Timeline"
        contentClassName="h-full"
      >
        <div className="h-full bg-white rounded-lg">
          <div className="p-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex justify-between items-center">
              <h2 className="text-base font-semibold text-gray-800">Project Timeline</h2>
              <div className="flex items-center space-x-4">
                {/* Zoom Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => handleZoom('in', e)}
                    className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
                    title="Zoom In (Ctrl +)"
                    disabled={zoomLevel <= 0.5}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5L12 19" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M5 12L19 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <span className="text-sm text-gray-600">{Math.round(zoomLevel * 100)}%</span>
                  <button
                    onClick={(e) => handleZoom('out', e)}
                    className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
                    title="Zoom Out (Ctrl -)"
                    disabled={zoomLevel >= 2}
                  >

                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12H19" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                <div className="flex -space-x-2">
                  {['Lt. Kumar', 'Lt. Sharma', 'Lt. Patel'].map((name, index) => (
                    <div key={index} className="w-6 h-6 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center">
                      <span className="text-[10px] text-white font-medium">{name.split(' ')[1][0]}</span>
                    </div>
                  ))}
                </div>
                <div className="h-4 border-l border-gray-300"></div>
                <div className="flex space-x-2">
                  <div className="flex items-center px-2 py-1 rounded-full bg-green-100">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></div>
                    <span className="text-[10px] font-medium text-green-700">Done</span>
                  </div>
                  <div className="flex items-center px-2 py-1 rounded-full bg-blue-100">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1.5"></div>
                    <span className="text-[10px] font-medium text-blue-700">Active</span>
                  </div>
                  <div className="flex items-center px-2 py-1 rounded-full bg-yellow-100">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mr-1.5"></div>
                    <span className="text-[10px] font-medium text-yellow-700">Pending</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="h-[calc(90vh-120px)] overflow-auto">
            <div className="sticky top-0 z-10 bg-white border-b">
              <div className="flex">
                <div className="w-44 py-2 px-3 border-r bg-gray-50 flex items-center">
                  <span className="text-xs font-medium text-gray-600">Task Details</span>
                </div>
                <div className="flex-1 overflow-x-auto">
                  <div className="flex px-3 py-2 bg-gray-50" style={{ minWidth: `${300 * zoomLevel}px` }}>
                    {(() => {
                      const rangeDays = (dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24);
                      const divisions = Math.min(Math.floor(rangeDays), 6);
                      const dayStep = rangeDays / divisions;

                      return Array.from({ length: divisions }).map((_, index) => {
                        const date = new Date(dateRange.start);
                        date.setDate(date.getDate() + (index * dayStep));
                        return (
                          <div
                            key={index}
                            className="flex-1 text-center relative group"
                            style={{ width: `${100 * zoomLevel}px` }}
                          >
                            <div className="text-[10px] font-medium text-gray-500">
                              {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-gray-200 cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity"></div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div>
              {tasks.map((task, taskIndex) => {
                const startDate = new Date(task.startDate);
                const endDate = new Date(task.endDate);
                const currentRangeStart = dateRange.start;
                const currentRangeEnd = dateRange.end;
                const totalDays = (currentRangeEnd - currentRangeStart) / (1000 * 60 * 60 * 24);
                const taskStart = (startDate - currentRangeStart) / (1000 * 60 * 60 * 24);
                const taskDuration = (endDate - startDate) / (1000 * 60 * 60 * 24);
                const startPercentage = Math.max(0, (taskStart / totalDays) * 100);
                const widthPercentage = Math.min(100 - startPercentage, (taskDuration / totalDays) * 100);

                const getStatusStyles = (status) => {
                  switch (status) {
                    case 'Completed':
                      return 'bg-gradient-to-r from-green-500 to-green-400 border-green-600';
                    case 'In Progress':
                      return 'bg-gradient-to-r from-blue-500 to-blue-400 border-blue-600';
                    case 'Delayed':
                      return 'bg-gradient-to-r from-red-500 to-red-400 border-red-600';
                    default:
                      return 'bg-gradient-to-r from-yellow-500 to-yellow-400 border-yellow-600';
                  }
                };

                return (
                  <div key={task.id}
                    className={`flex hover:bg-gray-50 group border-b ${taskIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <div className="w-44 py-2.5 px-3 border-r flex items-center">
                      <div className="min-w-0">
                        <div className="flex items-center space-x-2">
                          <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                          <div className="text-xs font-medium text-gray-900 truncate">
                            {task.name}
                          </div>
                        </div>
                        <div className="mt-1 flex items-center space-x-2">
                          <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-[9px] text-gray-600 font-medium">
                              {task.assignee.split(' ')[1][0]}
                            </span>
                          </div>
                          <div className="text-[10px] text-gray-500 truncate">
                            {formatDate(task.startDate)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 p-2 relative">
                      <div className="absolute inset-0 flex items-center px-2">
                        {task.precedingActivity && (
                          <div
                            className="absolute h-0.5 bg-gray-300"
                            style={{
                              left: `${startPercentage - 2}%`,
                              width: '2%',
                              top: '50%',
                              transform: 'translateY(-50%)'
                            }}
                          ></div>
                        )}
                        <div
                          className={`gantt-bar h-5 rounded-full shadow-sm border 
                            ${getStatusStyles(task.status)}
                            group-hover:shadow-md relative overflow-hidden`}
                          style={{
                            left: `${startPercentage}%`,
                            width: `${widthPercentage}%`,
                            transform: `scaleX(${zoomLevel})`,
                            transformOrigin: 'left'
                          }}
                        >
                          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-5 transition-opacity"></div>
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-[10px] text-white font-medium px-1.5 truncate shadow-sm">
                              {`${formatDate(task.startDate)} - ${formatDate(task.endDate)}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sticky bottom-0 border-t bg-gradient-to-r from-gray-50 to-white p-2 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="text-[10px] font-medium text-gray-600">
                  {tasks.length} Tasks
                </span>
              </div>
              <div className="flex space-x-4 text-[10px] text-gray-500">
                <span className="font-medium">Start: {dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                <span className="font-medium">End: {dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>

            {/* Modern Zoom Controls */}
            <div className="gantt-zoom-controls">
              <button
                className="gantt-zoom-btn"
                onClick={(e) => handleZoom('out', e)}
                disabled={zoomLevel <= 0.5}
                title="Zoom Out (Ctrl -)"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12H19" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="gantt-zoom-text">
                {Math.round(zoomLevel * 100)}%
              </div>
              <button
                className="gantt-zoom-btn"
                onClick={(e) => handleZoom('in', e)}
                disabled={zoomLevel >= 2}
                title="Zoom In (Ctrl +)"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5L12 19" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 12L19 12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                className="gantt-zoom-btn ml-1"
                onClick={resetZoom}
                title="Reset Zoom (Ctrl 0)"
                disabled={zoomLevel === 1 &&
                  dateRange.start.getTime() === dateRange.original.start.getTime() &&
                  dateRange.end.getTime() === dateRange.original.end.getTime()}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Add Task Modal with explicit wrapper */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-2xl animate-fade-in flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 bg-[#1e3a8a] text-white rounded-t-xl border-b border-gray-200 sticky top-0 z-10">
              <h3 className="text-xl font-bold tracking-wide">Add New Task</h3>
              <button onClick={handleCloseAddModal} className="text-white hover:text-gray-300">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              <TaskCreationForm
                isOpen={showAddModal}
                onClose={handleCloseAddModal}
                onSubmit={handleSubmitTask}
                tasks={tasks}
                taskData={currentTask}
                onChange={handleNewTaskChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Upload Task Modal */}
      <UploadTaskForm
        isOpen={showUploadModal}
        onClose={handleCloseUploadModal}
      />

      {/* Delete Confirmation Modal with explicit wrapper */}
      {deleteDialogVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-auto animate-fade-in" style={{ width: '30vw' }}>
            <ReusableForm
              headerText="Confirm Deletion"
              formData={deleteFormData}
              setFormData={setDeleteFormData}
              fields={deleteFormFields}
              onSubmit={confirmDelete}
              onHide={() => setDeleteDialogVisible(false)}
              loading={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MileStones;